import type { JSX, ReactNode } from 'react';

import { useUserProfile } from '../api/useUserProfile';

/**
 * IA-002 — client-side route access guard.
 *
 * Admin/exec/countersign routes previously rendered their full page chrome to
 * unauthorized roles on deep-link and sat on a permanent "Loading…" (the
 * backend 403 was never surfaced) — disclosing admin feature names + dead-
 * ending. This guard checks the resolved profile roles and renders an explicit
 * access state instead. The backend route gates remain the load-bearing
 * control; this is the UX/disclosure layer.
 *
 * Guarded on ROLE (not referrer), so authorized deep links (e.g. from a
 * notification email) still work. While the profile is loading we show a
 * neutral status; if the profile FETCH fails we surface an explicit error +
 * retry rather than silently denying (the nav-side of this is IA-006).
 */
export function RequireRoles({
  roles,
  children,
}: {
  roles: ReadonlyArray<string>;
  children: ReactNode;
}): JSX.Element {
  const { data: profile, isLoading, isError, refetch } = useUserProfile();

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-8 sm:px-8 sm:py-10 w-full">
        <p role="status" aria-live="polite" className="text-sm text-rr-secondary">
          Loading…
        </p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-8 sm:px-8 sm:py-10 w-full">
        <p role="alert" className="text-sm text-rr-error">
          We couldn’t load your account permissions.{' '}
          <button
            type="button"
            onClick={() => void refetch()}
            className="text-rr-accent-link underline hover:no-underline"
          >
            Retry
          </button>
        </p>
      </div>
    );
  }

  const userRoles = profile?.roles ?? [];
  const allowed = roles.some((r) => userRoles.includes(r));
  if (!allowed) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-8 sm:px-8 sm:py-10 w-full">
        <p role="alert" className="text-sm text-rr-error">
          You don’t have access to this page.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}

/** Role sets for the guard, mirroring the backend route gates + adminTools matrix. */
export const ADMIN_ROUTE_ROLES = ['superAdmin', 'opsAdmin'] as const;
export const SUPERADMIN_ROUTE_ROLES = ['superAdmin'] as const;
